import {EditorView} from '@codemirror/view'
import {
  BoldIcon,
  CodeIcon,
  CodeSquareIcon,
  HeadingIcon,
  ItalicIcon,
  LinkIcon,
  ListOrderedIcon,
  ListUnorderedIcon,
  QuoteIcon,
  StrikethroughIcon,
  TasklistIcon,
} from '@primer/octicons-react'
import {ActionBar} from '@primer/react'
import type {List, ListItem, Nodes} from 'mdast'

import {formatCodeBlock} from '../codemirror/commands/code-blocks/format-code-block'
import {isLabeledLink} from '../codemirror/commands/inline-formatting/format-link'
import {
  formatBlockquoteKeybinding,
  formatBoldKeybinding,
  formatHeading3Keybinding,
  formatInlineCodeKeybinding,
  formatItalicKeybinding,
  formatLinkKeybinding,
  formatOrderedListKeybinding,
  formatStrikethroughKeybinding,
  formatTaskListKeybinding,
  formatUnorderedListKeybinding,
} from '../codemirror/extensions/markdown-keymap'
import {findItemPrefixes} from '../language-tools/markdown/list-utils'
import styles from './Toolbar.module.css'
import {ToolbarButton} from './ToolbarButton'

interface ToolbarProps {
  view: EditorView | null
  fileUploadButton: React.ReactNode
}

export const Toolbar = ({view, fileUploadButton}: ToolbarProps) => {
  const isListItem = (node: Nodes): node is ListItem => node.type === 'listItem'
  const isTaskListItem = (node: Nodes): node is ListItem => isListItem(node) && !!findItemPrefixes(node).taskListPrefix
  const isTaskList = (node: Nodes): node is List =>
    node.type === 'list' && node.children.some(child => isTaskListItem(child as Nodes))

  return (
    <ActionBar aria-label="Formatting tools" className={styles.actionBar} gap="none">
      <ToolbarButton
        view={view}
        aria-label="Bold"
        icon={BoldIcon}
        command={formatBoldKeybinding}
        matchingNodeFilter={n => n.type === 'strong'}
      />
      <ToolbarButton
        view={view}
        aria-label="Italic"
        icon={ItalicIcon}
        command={formatItalicKeybinding}
        matchingNodeFilter={n => n.type === 'emphasis'}
      />
      <ToolbarButton
        view={view}
        aria-label="Link"
        icon={LinkIcon}
        command={formatLinkKeybinding}
        matchingNodeFilter={n => n.type === 'link' && isLabeledLink(n)}
      />
      <ToolbarButton
        view={view}
        aria-label="Code"
        icon={CodeIcon}
        command={formatInlineCodeKeybinding}
        matchingNodeFilter={n => n.type === 'inlineCode'}
      />
      <ToolbarButton
        view={view}
        aria-label="Quote"
        icon={QuoteIcon}
        command={formatBlockquoteKeybinding}
        matchingNodeFilter={n => n.type === 'blockquote'}
      />
      <ActionBar.Divider />
      <ToolbarButton
        view={view}
        aria-label="Unordered list"
        icon={ListUnorderedIcon}
        command={formatUnorderedListKeybinding}
        matchingNodeFilter={n => n.type === 'list' && !n.ordered && !isTaskList(n)}
      />
      <ToolbarButton
        view={view}
        aria-label="Ordered list"
        icon={ListOrderedIcon}
        command={formatOrderedListKeybinding}
        matchingNodeFilter={n => n.type === 'list' && !!n.ordered && !isTaskList(n)}
      />
      <ToolbarButton
        view={view}
        aria-label="Task list"
        icon={TasklistIcon}
        command={formatTaskListKeybinding}
        matchingNodeFilter={n => isTaskListItem(n)}
      />
      <ActionBar.Divider />
      {fileUploadButton}
      {/* <ToolbarButton onClick={() => {}} icon={ReplyIcon} aria-label="Saved replies" /> */}
      {/* <ToolbarButton onClick={() => {}} icon={DiffIgnoredIcon} aria-label="Slash commands" /> */}
      {/* <ToolbarButton onClick={() => {}} icon={AlertIcon} aria-label="Alert" /> */}
      <ToolbarButton
        view={view}
        aria-label="Code block"
        icon={CodeSquareIcon}
        command={formatCodeBlock}
        matchingNodeFilter={n => n.type === 'code'}
      />
      {/* <ToolbarButton onClick={() => {}} icon={TableIcon} aria-label="Table" /> */}
      {/* <ToolbarButton onClick={() => {}} icon={MultiSelectIcon} aria-label="Details" /> */}
      {/* <ToolbarButton onClick={() => {}} icon={SquareIcon} aria-label="Footnote" /> */}
      <ToolbarButton
        view={view}
        aria-label="Heading 3"
        icon={HeadingIcon}
        command={formatHeading3Keybinding}
        matchingNodeFilter={n => n.type === 'heading' && n.depth === 3}
      />
      {/* <ToolbarButton onClick={() => {}} icon={HorizontalRuleIcon} aria-label="Horizontal Rule" /> */}
      {/* <ToolbarButton onClick={() => {}} icon={NumberIcon} aria-label="Math block" /> */}
      {/* <ToolbarButton onClick={() => {}} icon={CrossReferenceIcon} aria-label="Reference" /> */}
      <ToolbarButton
        view={view}
        aria-label="Strikethrough"
        icon={StrikethroughIcon}
        command={formatStrikethroughKeybinding}
        matchingNodeFilter={n => n.type === 'delete'}
      />
    </ActionBar>
  )
}
Toolbar.displayName = 'HybridMarkdownEditor.Toolbar'
